<template>
  <div class="site-navigation-row">
    <div class="site-navigation-row__name">
      <Icon icon="fa-pro-light:arrow-turn-down-right" class="site-navigation-row__child-icon" />
      <span v-if="position" class="site-navigation-row__position">{{ position }}.</span>
      <div class="site-navigation-row__title">
        <span class="site-navigation-row__label">{{ label }}</span>
        <StatusLabel v-for="status in statuses" :key="status" :label="status" class="site-navigation-row__status" />
      </div>
    </div>

    <span v-if="item.key" class="site-navigation-row__module">{{ $t('menuItemModule') }}</span>
    <button
      v-else
      type="button"
      :class="['site-navigation-row__link', { 'site-navigation-row__link--missing': target.isMissing }]"
      :disabled="disabled"
      @click="$emit('edit')"
    >
      <Icon :icon="target.icon" />
      <span>{{ target.label }}</span>
    </button>

    <DropdownComponent :close-on-click="true" class="site-navigation-row__actions">
      <template #default>
        <ButtonComponent icon="fa-pro-light:angle-down" :disabled="disabled" />
      </template>
      <template #dropdown>
        <template v-for="(group, index) in actions">
          <hr v-if="index" :key="`separator-${index}`" />
          <ButtonComponent
            v-for="action in group"
            :key="action.label"
            :label="action.label"
            :icon="action.icon"
            :disabled="action.disabled"
            v-tooltip="{ content: action.tooltip, placement: 'left', boundariesElement: 'window' }"
            @click="action.onClick"
          />
        </template>
      </template>
    </DropdownComponent>

    <div class="site-navigation-row__handle">
      <Icon icon="fa-pro-light:arrows-up-down" />
    </div>
  </div>
</template>

<script>
import { DropdownComponent } from 'vue-elder-dropdown'
import StatusLabel from '@/components/StatusLabel'

export default {
  props: {
    item: Object,
    label: String,
    target: Object,
    position: Number,
    actions: Array,
    disabled: Boolean,
  },
  computed: {
    statuses() {
      const { key, hideInMenu, hideInFooter, placement } = this.item

      return [
        { label: this.$t('menuItemSystem'), condition: Boolean(key) },
        { label: this.$t('menuItemHidden'), condition: hideInMenu && hideInFooter },
        { label: this.$t('menuItemHiddenInMenu'), condition: hideInMenu && !hideInFooter },
        { label: this.$t('menuItemHiddenInFooter'), condition: hideInFooter && !hideInMenu },
        { label: this.$t('hamburgerMenu'), condition: placement === 'bar' },
      ]
        .filter(status => status.condition)
        .map(status => status.label)
    },
  },
  components: {
    DropdownComponent,
    StatusLabel,
  },
}
</script>

<style lang="scss">
.site-navigation-row {
  display: grid;
  grid-template-columns: var(--site-navigation-columns);
  align-items: center;
  gap: 1rem;
  min-height: 3.5rem;
  padding: 0.5rem 0.75rem 0.5rem 1rem;
  cursor: move;

  &:hover {
    outline: 1px solid var(--primary);
    outline-offset: -1px;
  }

  &__name {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }

  &__title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  &__label {
    overflow-wrap: anywhere;
  }

  &__child-icon,
  &__position,
  &__module {
    opacity: var(--opacity-muted);
  }

  &__position {
    min-width: 1.25rem;
    font-size: var(--font-size-small);
    font-variant-numeric: tabular-nums;
  }

  &__status.status-label {
    padding: 0.1rem 0.5rem;
    font-size: var(--font-size-small);
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    background-color: white;
    font: inherit;
    text-align: left;
    cursor: pointer;

    .kvass-icon {
      flex-shrink: 0;
      width: 1.25em;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &:hover {
      background-color: var(--lightest-grey);
    }

    &--missing {
      color: var(--error);
    }
  }

  &__module {
    padding: 0 0.75rem;
  }

  &__actions {
    .elder-dropdown__trigger {
      --vue-elder-button-padding-x: 0.75rem;
      --vue-elder-button-padding-y: 0.5rem;
    }

    .elder-dropdown__content {
      cursor: default;
    }
  }

  &__handle {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 2rem;
    height: 2rem;
    border-radius: var(--border-radius);
    background-color: var(--light-grey);
  }
}
</style>
